import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Chip,
    Typography,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CurrentPortfolioHistory from '../performance/CurrentPortfolioHistory';
import { mapOptimizerPortfolio } from '../hooks/useOptimizer';
import { assertPerformancePreparationCurrent, nextPerformanceProps, useFrozenPerformanceValue, usePerformancePreparationRegistration, preparePerformanceQuery, performancePreparationStamp } from '../performance/publicationPreparation';
import CompareExploreTab from '../performance/compareExplore/CompareExploreTab';
import EarningsPerformance from '../performance/EarningsPerformance';
import IntradayPriceObservations from '../performance/IntradayPriceObservations';
import DashboardToolbarPortal from '../components/dashboard/components/DashboardToolbarPortal';
import OptimizerPortfolioDropdown from '../components/optimizer/OptimizerPortfolioDropdown';
import { callBackend } from '../utils/api';
import { uppercaseLabel } from '../styles/typography';
import {
    ACTUAL_HOLDINGS_SOURCE, constantSourceQuery, constantSourceRequest, historicalRequestScope,
    holdingsSourceKey, holdingsSourceMatches, initialReplayInterval,
} from '../performance/historicalPerformanceBasis';
import { PORTFOLIO_HISTORY_CONTRACT, isHistoryContextConflict, syntheticHistoryFallbackReason } from '../performance/currentAttribution';
import { performanceSecondaryControlSx, performanceToggleButtonSx, performanceToggleGroupSx } from '../styles/performanceMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
} from '../styles/msiStyleTokens';

// Workspace indices, in toolbar order.
const TAB = Object.freeze({ historical: 0, intraday: 1, earnings: 2, compare: 3 });
const TAB_LABELS = [
    'Historical',
    'Intraday',
    'Earnings P&L',
    'Compare & Explore',
];
const TAB_ICONS = [AutoStoriesRoundedIcon, BoltRoundedIcon, DescriptionOutlinedIcon, CompareArrowsRoundedIcon];
// The workspaces that read the one selected portfolio source.
const SINGLE_SOURCE_TABS = Object.freeze([TAB.historical, TAB.intraday, TAB.earnings]);
// Built-in choices are typed, namespaced entries, never saved-portfolio IDs.
const SOURCE_ENTRY = Object.freeze({ actual: 'performance:actual', synthetic: 'performance:synthetic', ibkr: 'performance:ibkr' });
const NO_PORTFOLIOS = Object.freeze([]);
const reasonText = (reasons) => (reasons || []).map((reason) => typeof reason === 'string' ? reason : reason.message || reason.reason_code).filter(Boolean).join(' ');
const SUB_TAB_LABEL_SX = uppercaseLabel({
    color: 'rgb(var(--c-text))',
    fontSize: '0.76rem',
    letterSpacing: '0.08em',
});
async function readPerformanceOptions(constantSource, signal) {
            const query = constantSourceQuery(constantSource);
            const response = await callBackend(`api/performance/history/options${query ? `?${query}` : ''}`, 'GET', null, { signal });
            const constantAvailable = Array.isArray(response?.choices) && response.choices.find((choice) => choice.value === 'constant')?.available;
            if (response?.calculation_contract !== PORTFOLIO_HISTORY_CONTRACT || !Array.isArray(response.choices)
                || !holdingsSourceMatches(response.constant_source, constantSource)
                || (constantAvailable !== false && !response.context?.context_digest)) {
                throw new Error('The shared performance service is not ready. Refresh after the application update completes.');
            }
            return response;
}

export default function Performance(inputProps) {
    const [publicationBundle, setPublicationBundle] = useState(null);
    const incomingContextKey = historicalRequestScope(inputProps);
    const acceptedTarget = publicationBundle?.contextKey === incomingContextKey
        && publicationBundle.userUid === inputProps.userUid
        && publicationBundle.factorSliceScopeKey === inputProps.factorSliceScopeKey;
    const retainedProps = useRef(inputProps);
    if (!inputProps.publicationPending || acceptedTarget || retainedProps.current.userUid !== inputProps.userUid
        || retainedProps.current.factorSliceScopeKey !== inputProps.factorSliceScopeKey) {
        retainedProps.current = inputProps;
    }
    const props = inputProps.publicationPending && !acceptedTarget
        ? { ...retainedProps.current, publicationPending: true,
            onRegisterPublicationPreparation: inputProps.onRegisterPublicationPreparation }
        : acceptedTarget ? { ...inputProps, initialContextPending: false, initialContextFailed: false } : inputProps;
    const [preparing, setPreparing] = useState(null);
    const [preparationReady, setPreparationReady] = useState(false);
    const preparationAttemptRef = useRef(null);
    const childPreparations = useRef(new Map());
    const registerHistory = useCallback(value => { childPreparations.current.set(TAB.historical, value); }, []);
    const registerEarnings = useCallback(value => { childPreparations.current.set(TAB.earnings, value); }, []);
    const registerIntraday = useCallback(value => { childPreparations.current.set(TAB.intraday, value); }, []);
    const registerCompare = useCallback(value => { childPreparations.current.set(TAB.compare, value); }, []);
    const queryClient = useQueryClient();
    const contextKey = historicalRequestScope(props);
    const activeContextRef = useRef(null);
    if (activeContextRef.current?.key !== contextKey || activeContextRef.current?.factorSliceScopeKey !== props.factorSliceScopeKey) {
        activeContextRef.current = { key: contextKey, factorSliceScopeKey: props.factorSliceScopeKey, recovered: new Set() };
    }
    const requestScope = activeContextRef.current;
    const publicationPreparing = Boolean(props.publicationPending || preparing?.scope === requestScope);
    const publicationPreparingRef = useRef(publicationPreparing); publicationPreparingRef.current = publicationPreparing;
    const acceptedBundle = acceptedTarget ? publicationBundle : null;
    const [rawSurface, setSurface] = useState(null);
    const surface = rawSurface;
    const tab = surface?.userUid === props.userUid ? surface.tab : TAB.historical;
    const [visited, setVisited] = useState(null);
    const visitedTabs = visited?.contextKey === contextKey ? visited.tabs : [tab];
    const activeTabRef = useRef(tab); activeTabRef.current = tab;
    const setTab = (next) => {
        const attempt = preparationAttemptRef.current;
        if (attempt?.scope === requestScope && attempt.phase === 'prepared') return;
        if (attempt?.scope === requestScope && attempt.phase === 'preparing') attempt.select(next);
        setSurface({ userUid: props.userUid, tab: next });
        setVisited((old) => ({ contextKey, tabs: [...new Set([...(old?.contextKey === contextKey ? old.tabs : [tab]), next])] }));
    };
    // Only user-visited workspaces join this attempt. Keep each requested
    // participant mounted/active while hiding it, so navigation does not retire
    // its one target read. All ordinary queries remain paused below.
    const workspaceActive = (workspace) => tab === workspace || Boolean(
        preparing?.scope === requestScope && preparing.tabs.has(workspace));
    const navigationLocked = preparationReady && preparing?.scope === requestScope;
    useLayoutEffect(() => {
        const attempt = preparationAttemptRef.current;
        if (attempt?.scope === requestScope && attempt.phase === 'preparing') attempt.activate(tab);
    }, [tab, preparing, requestScope]);
    useLayoutEffect(() => () => {
        const attempt = preparationAttemptRef.current;
        if (attempt?.scope === requestScope && attempt.phase !== 'committed') attempt.retire();
    }, [requestScope]);
    const [rawSelection, setSelection] = useState(null);
    const selection = useFrozenPerformanceValue(rawSelection, publicationPreparing, contextKey);
    const selectionCurrent = selection?.contextKey === contextKey;
    // A chosen Position Sizing or saved portfolio is a stable UI identity: it survives
    // Actual, Watchlist and factor-context changes for the same user, and its own
    // options re-validate it. Execution and Actual choices stay scoped to one context.
    const keptSource = selection?.userUid === props.userUid && selection.basis === 'constant'
        && selection.source && selection.source.kind !== 'actual' ? selection.source : null;
    const portfolioBasis = selectionCurrent ? selection.basis : keptSource ? 'constant' : 'auto';
    // One active choice: execution sources replay Actual; another portfolio is Constant only.
    const holdingsSource = (selectionCurrent ? selection.basis === 'constant' && selection.source : keptSource)
        || ACTUAL_HOLDINGS_SOURCE;
    const sourceKey = holdingsSourceKey(holdingsSource);
    const constantSource = useMemo(() => constantSourceRequest(holdingsSource), [holdingsSource]);
    const savedPortfolios = props.listOfWhatIfPortfolios || NO_PORTFOLIOS;
    const savedSource = holdingsSource.kind === 'position_sizing' ? savedPortfolios.find((item) => item?.type === 'wi')
        : holdingsSource.kind === 'optimized' ? savedPortfolios.find((item) => item?.type === 'opt' && String(item.id) === String(holdingsSource.portfolioId))
            : null;
    // A discovered save, edit or publication re-validates the same selection.
    const sourceRevision = holdingsSource.kind === 'optimized' ? savedSource?.portfolioVersion ?? null
        : holdingsSource.kind === 'position_sizing' ? savedSource?.currentPosSizingStateHash ?? null : null;
    const actionScopeRef = useRef(null);
    const actionKey = `${contextKey}:${portfolioBasis}:${sourceKey}`;
    if (actionScopeRef.current?.key !== actionKey) actionScopeRef.current = { key: actionKey };
    const actionScope = actionScopeRef.current;
    const [rawAnalysisWindow, setAnalysisWindow] = useState(null);
    const analysisWindow = useFrozenPerformanceValue(rawAnalysisWindow, publicationPreparing, contextKey);
    const lookback = analysisWindow?.contextKey === contextKey ? analysisWindow.lookback : initialReplayInterval(props.factorContext?.factor_as_of_date);
    const [rawAnalysisResult, setAnalysisResult] = useState(null);
    const analysisResult = useFrozenPerformanceValue(rawAnalysisResult, publicationPreparing, contextKey);
    const [rawSourceAction, setSourceAction] = useState(null);
    const sourceAction = useFrozenPerformanceValue(rawSourceAction, publicationPreparing, contextKey);
    useEffect(() => {
        if (!sourceAction?.success) return undefined;
        const timeout = setTimeout(() => {
            setSourceAction((old) => old?.token === sourceAction.token ? null : old);
        }, 5000);
        return () => clearTimeout(timeout);
    }, [sourceAction]);
    const [rawIbkrAction, setIbkrAction] = useState(null);
    const ibkrAction = useFrozenPerformanceValue(rawIbkrAction, publicationPreparing, contextKey);
    const ibkrAttemptRef = useRef(null);
    useEffect(() => () => {
        const attempt = ibkrAttemptRef.current;
        if (attempt?.scope === requestScope) {
            ibkrAttemptRef.current = null;
            attempt.controller.abort();
        }
    }, [requestScope]);
    const visibleIbkrAction = ibkrAction?.scope === requestScope ? ibkrAction : null;
    const [rawRegeneratedSource, setRegeneratedSource] = useState(null);
    const regeneratedSource = useFrozenPerformanceValue(rawRegeneratedSource, publicationPreparing, contextKey);
    const [rawHistoryRefresh, setHistoryRefresh] = useState(0);
    const historyRefresh = useFrozenPerformanceValue(rawHistoryRefresh, publicationPreparing, contextKey);
    const [rawAuthorityRefresh, setAuthorityRefresh] = useState(null);
    const authorityRefresh = useFrozenPerformanceValue(rawAuthorityRefresh, publicationPreparing, contextKey);
    // History sources describe an accepted Watchlist/factor context. Actual can
    // still be empty: a saved or Position Sizing portfolio may be available.
    const sourceContextReady = Boolean(props.userUid && props.watchlistIsReady === true
        && props.factorContext?.factor_release_id && props.factorContext?.factor_as_of_date
        && !props.initialContextPending && !props.initialContextFailed);
    const sourceContextReadyRef = useRef(sourceContextReady);
    sourceContextReadyRef.current = sourceContextReady;
    useEffect(() => {
        if (sourceContextReady || !ibkrAttemptRef.current) return;
        ibkrAttemptRef.current.controller.abort();
        ibkrAttemptRef.current = null;
        setIbkrAction(null);
    }, [sourceContextReady]);
    // Disabled workspaces stay mounted. Unsubscribe so their unused reads abort,
    // while another observer of the same cache entry can keep its request alive.
    const optionsRead = useQuery({
        queryKey: ['portfolio_history_options', contextKey, sourceKey, sourceRevision],
        queryFn: async ({ signal }) => {
            const response = await readPerformanceOptions(constantSource, signal);
            if (activeContextRef.current !== requestScope) throw new Error('The portfolio context changed. Refresh the available history sources.');
            return response;
        },
        // Another source's choices in the same context stay presentation only while
        // this source loads; they never cross a user or context change.
        placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === contextKey ? previous : undefined),
        enabled: sourceContextReady && SINGLE_SOURCE_TABS.includes(tab) && !publicationPreparing, subscribed: sourceContextReady && SINGLE_SOURCE_TABS.includes(tab) && !publicationPreparing, retry: false, staleTime: 300_000, gcTime: 300_000, refetchOnWindowFocus: true, refetchOnReconnect: true,
        refetchIntervalInBackground: false,
        refetchInterval: (query) => sourceContextReady && !query.state.error ? 300_000 : false,
    });
    const optionsQuery = useFrozenPerformanceValue(optionsRead, publicationPreparing, performancePreparationStamp([contextKey, sourceKey, sourceRevision]));
    const sourceVisit = useRef(null);
    useEffect(() => {
        if (!sourceContextReady || publicationPreparing || !SINGLE_SOURCE_TABS.includes(tab)) { sourceVisit.current = null; return; }
        if (sourceVisit.current?.tab === tab && sourceVisit.current.scope === requestScope) return;
        sourceVisit.current = { tab, scope: requestScope };
        if (optionsQuery.isStale && !optionsQuery.isFetching) void optionsQuery.refetch();
    }, [tab, sourceContextReady, publicationPreparing, requestScope, optionsQuery.isStale, optionsQuery.isFetching, optionsQuery.refetch]);
    const acceptedRefresh = authorityRefresh?.scope === requestScope ? authorityRefresh : null;
    const analysisVersion = historyRefresh + (acceptedRefresh?.version || 0);
    const options = sourceContextReady && !optionsQuery.error && !optionsQuery.isPlaceholderData
        && (!requestScope.refreshToken || acceptedRefresh?.token === requestScope.refreshToken) ? optionsQuery.data : null;
    const sourcePending = Boolean(sourceContextReady && !optionsQuery.error && (optionsQuery.isPending || optionsQuery.isPlaceholderData));
    const selectedChoice = options?.choices.find((choice) => choice.value === portfolioBasis);
    const acceptedAnalysis = analysisResult?.contextKey === contextKey ? analysisResult : acceptedBundle?.analysisResults?.[tab];
    const currentResult = sourceContextReady && acceptedAnalysis?.contextKey === contextKey && acceptedAnalysis.basis === portfolioBasis
        && acceptedAnalysis.source === sourceKey ? acceptedAnalysis.data : null;
    const partialFallback = syntheticHistoryFallbackReason(currentResult);
    // `auto` remains an internal first-entry history check, never a visible
    // portfolio. Cached choices are presentation only while authority reloads.
    const presentationChoices = sourceContextReady ? optionsQuery.data?.choices || [] : [];
    const constantChoice = presentationChoices.find((choice) => choice.value === 'constant')
        || { value: 'constant', label: 'Constant portfolio', available: true };
    const realChoice = presentationChoices.find((choice) => choice.value === 'ibkr');
    const syntheticChoice = presentationChoices.find((choice) => choice.value === 'synthetic')
        || { value: 'synthetic', label: 'Synthetic IBKR portfolio', available: false };
    const resolvedBasis = partialFallback ? 'constant'
        : portfolioBasis === 'auto'
            ? (currentResult?.basis_resolution?.selected || (realChoice?.available ? 'ibkr' : 'constant'))
            : portfolioBasis === 'ibkr'
                ? (visibleIbkrAction?.pending ? 'ibkr' : currentResult?.basis_resolution?.selected || 'ibkr')
                : [constantChoice, syntheticChoice].some((choice) => choice.value === portfolioBasis && choice.available) ? portfolioBasis : 'constant';
    const historyReadEnabled = Boolean(options?.context?.context_digest && selectedChoice?.available && !visibleIbkrAction?.pending);
    // An unavailable portfolio stays selected with its reason; nothing substitutes Actual.
    const unavailableConstant = resolvedBasis === 'constant' ? options?.choices.find((choice) => choice.value === 'constant') : null;
    const sourceErrorText = unavailableConstant?.available === false ? reasonText(unavailableConstant.reasons) || 'This portfolio is unavailable.' : null;
    const sourceError = useMemo(() => (sourceErrorText
        ? Object.assign(new Error(sourceErrorText), { code: 'constant_source_unavailable' }) : null), [sourceErrorText]);
    const ibkrWarning = visibleIbkrAction?.pending ? null : visibleIbkrAction?.connectionFailed
        ? `Unable to connect to IBKR. ${resolvedBasis === 'constant' ? 'Showing Constant portfolio.' : 'Using saved trades.'}`
        : visibleIbkrAction?.analysisUnavailable
            ? 'IBKR trades were fetched, but aren’t ready for analysis. Showing Constant portfolio.' : null;
    const source = regeneratedSource?.contextKey === contextKey ? regeneratedSource.source : currentResult?.source;
    const syntheticSource = source?.kind === 'synthetic_ibkr_history.v1' ? source : null;
    const expectedSource = regeneratedSource?.contextKey === contextKey ? regeneratedSource.source : portfolioBasis === 'synthetic' ? options?.synthetic : null;
    const requestExpectation = useMemo(() => ({ expected_context_digest: options?.context?.context_digest,
        ...(constantSource ? { constant_portfolio_source: constantSource } : {}),
        ...(expectedSource?.fixture_id ? { expected_source_fixture_id: expectedSource.fixture_id, expected_source_generation: expectedSource.generation } : {}),
    }), [options?.context?.context_digest, constantSource, expectedSource?.fixture_id, expectedSource?.generation]);
    const receiveResult = useCallback((workspace, data) => {
        if (publicationPreparingRef.current || activeTabRef.current !== workspace || activeContextRef.current !== requestScope || actionScopeRef.current !== actionScope) return;
        setAnalysisResult({ contextKey, basis: portfolioBasis, source: sourceKey, data });
    }, [publicationPreparing, contextKey, portfolioBasis, sourceKey, requestScope, actionScope]);
    const receiveHistoryResult = useCallback((data) => receiveResult(TAB.historical, data), [receiveResult]);
    const receiveEarningsResult = useCallback((data) => receiveResult(TAB.earnings, data), [receiveResult]);
    const chooseBasis = (basis, source = ACTUAL_HOLDINGS_SOURCE) => {
        if (publicationPreparingRef.current) return;
        if (basis !== 'ibkr') {
            ibkrAttemptRef.current?.controller.abort();
            ibkrAttemptRef.current = null;
            setIbkrAction(null);
        }
        setSelection({ userUid: props.userUid, contextKey, basis, source });
        if (basis === portfolioBasis && holdingsSourceKey(source) === sourceKey) setHistoryRefresh((value) => value + 1);
        setAnalysisResult(null); setRegeneratedSource(null); setSourceAction(null);
    };
    const connectRealIbkr = async () => {
        // A connection reads no Constant source; it can start while another source loads or fails.
        if (publicationPreparingRef.current || !sourceContextReady || ibkrAttemptRef.current) return;
        const attempt = { scope: requestScope, controller: new AbortController() };
        ibkrAttemptRef.current = attempt;
        const isCurrent = () => sourceContextReadyRef.current && activeContextRef.current === requestScope && ibkrAttemptRef.current === attempt;
        chooseBasis('ibkr');
        setIbkrAction({ scope: requestScope, pending: true });
        let connected = false;
        const timeout = setTimeout(() => attempt.controller.abort(), 50_000);
        try {
            const result = await callBackend('api/ibkr/sync-history', 'POST', { execution_source: 'real' }, { signal: attempt.controller.signal });
            if (!isCurrent()) return;
            connected = result?.status === 'success' && result?.execution_source === 'real';
        } catch {
            if (!isCurrent()) return;
        } finally {
            clearTimeout(timeout);
        }
        if (!isCurrent()) return;
        const refreshed = await optionsQuery.refetch();
        if (!isCurrent()) return;
        const realReady = !refreshed.error && refreshed.data?.choices?.some((choice) => choice.value === 'ibkr' && choice.available);
        ibkrAttemptRef.current = null;
        setSelection({ userUid: props.userUid, contextKey, basis: realReady ? 'ibkr' : 'constant' });
        setAnalysisResult(null);
        setHistoryRefresh((value) => value + 1);
        setIbkrAction({ scope: requestScope, pending: false, connectionFailed: !connected, analysisUnavailable: connected && !realReady });
    };
    const selectBasis = (basis) => { if (basis === 'ibkr') void connectRealIbkr(); else chooseBasis(basis); };
    const retryHistory = useCallback(() => {
        if (publicationPreparingRef.current || !sourceContextReady || activeContextRef.current !== requestScope) return;
        const token = {};
        // Query-cache notifications can render before queued React state. Fence
        // those renders until the replacement key and authority publish together.
        requestScope.refreshToken = token;
        setRegeneratedSource(null); setAnalysisResult(null);
        setAuthorityRefresh((old) => ({ scope: requestScope, token: null, version: old?.scope === requestScope ? old.version : 0 }));
        void optionsQuery.refetch().finally(() => {
            if (publicationPreparingRef.current || activeContextRef.current !== requestScope || requestScope.refreshToken !== token) return;
            setAuthorityRefresh((old) => ({ scope: requestScope, token, version: (old?.scope === requestScope ? old.version : 0) + 1 }));
        });
    }, [optionsQuery.refetch, requestScope, sourceContextReady]);
    // One automatic recovery per selected source in this context; repeats need an explicit Retry.
    const recoveryKeyRef = useRef(sourceKey); recoveryKeyRef.current = sourceKey;
    const reconcileAuthority = useCallback(() => {
        if (!sourceContextReady || activeContextRef.current !== requestScope || requestScope.recovered.has(recoveryKeyRef.current)) return;
        requestScope.recovered.add(recoveryKeyRef.current);
        retryHistory();
    }, [requestScope, retryHistory, sourceContextReady]);
    const refreshHistoryAuthority = useCallback(() => { if (activeTabRef.current === TAB.historical && actionScopeRef.current === actionScope) retryHistory(); }, [retryHistory, actionScope]);
    const refreshEarningsAuthority = useCallback(() => { if (activeTabRef.current === TAB.earnings && actionScopeRef.current === actionScope) retryHistory(); }, [retryHistory, actionScope]);
    const refreshIntradayAuthority = useCallback(() => { if (activeTabRef.current === TAB.intraday && actionScopeRef.current === actionScope) retryHistory(); }, [retryHistory, actionScope]);
    const reconcileHistoryAuthority = useCallback(() => { if (activeTabRef.current === TAB.historical && actionScopeRef.current === actionScope) reconcileAuthority(); }, [reconcileAuthority, actionScope]);
    const reconcileEarningsAuthority = useCallback(() => { if (activeTabRef.current === TAB.earnings && actionScopeRef.current === actionScope) reconcileAuthority(); }, [reconcileAuthority, actionScope]);
    const regenerateSynthetic = async () => {
        if (publicationPreparingRef.current || sourceAction?.pending || !options?.context?.context_digest || portfolioBasis !== 'synthetic') return;
        const token = {};
        const isCurrent = () => activeContextRef.current === requestScope && actionScopeRef.current === actionScope;
        setSourceAction({ contextKey, token, pending: true });
        try {
            const response = await callBackend('api/performance/history/synthetic/regenerate', 'POST', {
                portfolio_basis: 'synthetic', lookback,
                expected_factor_release_id: props.factorContext?.factor_release_id,
                expected_factor_context_id: props.factorContext?.factor_context_id || null,
                expected_context_digest: options.context.context_digest,
                expected_fixture_id: syntheticSource?.fixture_id || options.synthetic?.fixture_id || null,
                expected_generation: syntheticSource?.generation || options.synthetic?.generation || null,
                idempotency_key: crypto.randomUUID(),
            });
            if (!isCurrent()) return;
            if (response?.success !== true || response?.context?.context_digest !== options.context.context_digest
                || response?.source?.kind !== 'synthetic_ibkr_history.v1' || response.source.real_execution_writes !== 0
                || !response.source.fixture_id || !Number.isInteger(response.source.generation)) {
                throw new Error('The replacement synthetic history could not be confirmed. Refresh before trying again.');
            }
            const prefix = [PORTFOLIO_HISTORY_CONTRACT, props.userUid || '', props.watchlistVersion, props.actualPortfolioVersion, props.factorContext || null];
            await Promise.all(['current_portfolio_history', 'current_portfolio_history_detail'].map((key) => queryClient.invalidateQueries({ queryKey: [key, ...prefix], refetchType: 'none' })));
            if (!isCurrent()) return;
            // Every source's options in this context describe the same Actual synthetic ledger.
            queryClient.setQueriesData({ queryKey: ['portfolio_history_options', contextKey] }, (old) => old ? { ...old, synthetic: { ...old.synthetic, ...response.source } } : old);
            setRegeneratedSource({ contextKey, source: response.source });
            setAnalysisResult(null); setHistoryRefresh((value) => value + 1);
            setSourceAction((old) => old?.token === token ? { ...old, pending: false, success: true } : old);
        } catch (error) {
            if (isCurrent()) setSourceAction((old) => old?.token === token ? { ...old, pending: false, error: error.message || 'Synthetic history could not be regenerated.' } : old);
        }
    };
    const visibleSourceAction = sourceAction?.contextKey === contextKey ? sourceAction : null;
    const [intradaySelection, setIntradaySelection] = useState({ userUid: props.userUid, mode: 'returns' });
    const intradayMode = intradaySelection.userUid === props.userUid ? intradaySelection.mode : 'returns';
    const tabs = TAB_LABELS.map((label, id) => ({ label, id, Icon: TAB_ICONS[id] }));

    const intradayModeControls = <ToggleButtonGroup size="small" exclusive disabled={publicationPreparing} value={intradayMode}
        aria-label="Intraday analysis mode"
        onChange={(_, mode) => mode && setIntradaySelection({ userUid: props.userUid, mode })}
        sx={{ ...performanceToggleGroupSx, maxWidth: '100%' }}>
        <ToggleButton value="returns" sx={performanceToggleButtonSx}>Returns</ToggleButton>
        <ToggleButton value="prices" sx={performanceToggleButtonSx}>Stored Prices</ToggleButton>
    </ToggleButtonGroup>;

    // Actual availability travels with every source's options as the `auto` choice.
    const actualChoice = presentationChoices.find((choice) => choice.value === 'auto');
    const sourceLabel = holdingsSource.kind === 'position_sizing' ? 'Position Sizing · Constant'
        : holdingsSource.kind === 'optimized'
            ? `Optimized: ${savedSource?.value || 'saved portfolio'} · Constant` : 'Actual · Constant';
    const selectedSourceValue = resolvedBasis === 'synthetic' ? SOURCE_ENTRY.synthetic : resolvedBasis === 'ibkr' ? SOURCE_ENTRY.ibkr
        : holdingsSource.kind === 'actual' ? SOURCE_ENTRY.actual : savedSource ? String(savedSource.id) : `performance:unavailable:${sourceKey}`;
    const performanceSources = {
        label: 'Portfolio basis',
        // Choices wait for the first load and a conflict refresh. Switching portfolios
        // stays possible while one loads, and a failed read never traps the user.
        disabled: publicationPreparing || !sourceContextReady || (!optionsQuery.error && (!optionsQuery.data
            || Boolean(requestScope.refreshToken && acceptedRefresh?.token !== requestScope.refreshToken))),
        selectedValue: selectedSourceValue,
        selectedLabel: { [SOURCE_ENTRY.synthetic]: syntheticChoice.label, [SOURCE_ENTRY.ibkr]: 'Interactive Brokers portfolio' }[selectedSourceValue] || sourceLabel,
        entries: [
            // Like a saved portfolio, an unavailable Actual stays selectable and explains itself.
            { value: SOURCE_ENTRY.actual, label: 'Actual · Constant', group: 'constant', disabled: false,
                detail: actualChoice?.available === false ? reasonText(actualChoice.reasons) : null },
            { value: SOURCE_ENTRY.synthetic, label: syntheticChoice.label, group: 'execution', disabled: !syntheticChoice.available },
            // Real IBKR is always selectable for a connection attempt. Its stored-data
            // availability still governs whether the analysis may read that source.
            { value: SOURCE_ENTRY.ibkr, label: 'Interactive Brokers portfolio', group: 'execution', disabled: false },
        ],
        // A deleted saved portfolio stays selected and unavailable until another is chosen.
        missingSelection: holdingsSource.kind !== 'actual' && !savedSource ? { value: selectedSourceValue, label: `${sourceLabel} (unavailable)` } : null,
        portfolioLabel: (item) => (item?.type === 'wi' ? 'Position Sizing' : item.value),
        onSelectEntry: (value) => selectBasis(value === SOURCE_ENTRY.ibkr ? 'ibkr' : value === SOURCE_ENTRY.synthetic ? 'synthetic' : 'constant'),
        onSelectPortfolio: (item) => chooseBasis('constant', item.type === 'wi'
            ? { kind: 'position_sizing' } : { kind: 'optimized', portfolioId: Number(item.id) }),
    };

    const intradaySnapshot = props.factorContext?.factor_as_of_date && <Tooltip title={[props.factorContext.vendor, props.factorContext.factor_model || props.factorContext.model, props.factorContext.factor_as_of_date].filter(Boolean).join(' · ')}>
        <Chip size="small" variant="outlined" sx={{ fontSize: 11 }} label={`Factor snapshot: ${props.factorContext.factor_as_of_date}`} />
    </Tooltip>;
    const sourceControls = <>
        <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>{TAB_LABELS[tab]}</Typography>
        {tab === TAB.intraday && intradayModeControls}
        <Box sx={{ width: 280, maxWidth: '100%', flexShrink: 0 }}>
            <OptimizerPortfolioDropdown hideTitle compact listOfWhatIfPortfolios={savedPortfolios}
                fetchOptimizerPortfolios={props.fetchOptimizerPortfolios}
                // Explicit, reviewed Watchlist recovery from saved-portfolio details.
                onWatchlistRestorationStaged={props.onWatchlistRestorationStaged}
                restrictedPortfolioNames={NO_PORTFOLIOS} restrictedDeletePortfolioNames={NO_PORTFOLIOS}
                performanceSources={performanceSources} />
        </Box>
        <Box sx={{ width: 18, height: 18, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {sourceContextReady && optionsQuery.isFetching && <CircularProgress size={14} aria-label="Loading history sources" />}
        </Box>
        {visibleIbkrAction?.pending && <Box role="status" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><CircularProgress size={14} /><Typography variant="caption">Connecting to IBKR…</Typography></Box>}
        {ibkrWarning && <Box role="status" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}><WarningAmberOutlinedIcon fontSize="small" /><Typography variant="caption">{ibkrWarning}</Typography>{tab === TAB.intraday && <Button size="small" onClick={() => { void connectRealIbkr(); }}>Retry IBKR</Button>}</Box>}
        {tab !== TAB.intraday && portfolioBasis === 'synthetic' && !partialFallback && <Button aria-label="Regenerate synthetic trades" onClick={() => { void regenerateSynthetic(); }} disabled={!options || visibleSourceAction?.pending} sx={performanceSecondaryControlSx}>
            Regenerate synthetic trades
            <Box component="span" role="status" sx={{ width: 18, height: 18, ml: 0.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {visibleSourceAction?.pending && <CircularProgress size={14} aria-label="Regenerating synthetic trades" />}
                {visibleSourceAction?.success && <CheckCircleRoundedIcon titleAccess="Synthetic trades regenerated" sx={{ fontSize: 16, color: 'success.main' }} />}
            </Box>
        </Button>}
        {tab !== TAB.intraday && <Box sx={{ ml: tab === TAB.historical ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!partialFallback && (portfolioBasis === 'synthetic' || syntheticSource) && <Tooltip title="Simulated trades; not your account performance.">
                <Chip tabIndex={0} size="small" color="warning" variant="outlined" aria-label="Synthetic IBKR data" label={`Synthetic data${syntheticSource ? ` · generation ${syntheticSource.generation}` : ''}`} />
            </Tooltip>}
        </Box>}
    </>;

    const preparationStamp = performancePreparationStamp([contextKey, props.factorSliceScopeKey, portfolioBasis, sourceKey,
        sourceRevision, lookback, intradayMode, analysisVersion, rawHistoryRefresh, rawIbkrAction?.pending, rawSourceAction?.pending, props.compareExploreColumns]);
    const preparationRegistration = usePerformancePreparationRegistration(inputProps.onRegisterPublicationPreparation,
        async ({ preparedTarget, isCurrentRequest, isPublicationCurrent }) => {
            if (rawIbkrAction?.scope === requestScope && rawIbkrAction.pending
                || rawSourceAction?.contextKey === contextKey && rawSourceAction.pending) throw new Error('Finish the current Performance source action before preparing a factor change.');
            const scope = requestScope; const selectedAction = actionScope;
            if (props.factorSliceScopeKey && preparedTarget.factorSliceScopeKey !== props.factorSliceScopeKey) {
                throw new Error('Performance preparation belongs to another user session.');
            }
            const targetProps = nextPerformanceProps(props, preparedTarget);
            const targetKey = historicalRequestScope(targetProps);
            const targetList = preparedTarget.optimizerSummaries?.portfolios;
            if (Array.isArray(targetList)) targetProps.listOfWhatIfPortfolios = targetList.map(mapOptimizerPortfolio);
            const nextSource = keptSource || ACTUAL_HOLDINGS_SOURCE;
            const nextBasis = keptSource ? 'constant' : 'auto';
            const nextSourceKey = holdingsSourceKey(nextSource);
            const nextConstant = constantSourceRequest(nextSource);
            const nextSaved = nextSource.kind === 'position_sizing'
                ? targetProps.listOfWhatIfPortfolios?.find(item => item.type === 'wi')
                : nextSource.kind === 'optimized' ? targetProps.listOfWhatIfPortfolios?.find(item => item.type === 'opt' && String(item.id) === String(nextSource.portfolioId)) : null;
            const nextSourceRevision = nextSource.kind === 'optimized' ? nextSaved?.portfolioVersion ?? null
                : nextSource.kind === 'position_sizing' ? nextSaved?.currentPosSizingStateHash ?? null : null;
            const initialTab = activeTabRef.current;
            const attempt = { scope, phase: 'preparing', tabs: new Set([initialTab]), selected: initialTab };
            let discarded = false; let committed = false; let wake = null;
            const activations = new Map(); const activated = new Set([initialTab]);
            const workspaces = new Map(); let optionsPromise;
            const current = () => !discarded && preparationAttemptRef.current === attempt
                && (committed ? isPublicationCurrent() : isCurrentRequest())
                && activeContextRef.current?.factorSliceScopeKey === scope.factorSliceScopeKey && (
                committed ? activeContextRef.current?.key === targetKey || activeContextRef.current === scope
                    : activeContextRef.current === scope && actionScopeRef.current === selectedAction
            );
            const signalSelection = () => { const notify = wake; wake = null; notify?.(); };
            attempt.select = (workspace) => {
                if (attempt.phase !== 'preparing' || attempt.selected === workspace) return;
                attempt.tabs.add(workspace); attempt.selected = workspace; signalSelection();
            };
            attempt.activate = (workspace) => {
                activated.add(workspace); activations.get(workspace)?.(); activations.delete(workspace);
            };
            attempt.retire = () => {
                discarded = true; attempt.phase = 'discarded'; signalSelection();
                activations.forEach(resolve => resolve()); activations.clear();
            };
            const clearPreparing = () => {
                if (preparationAttemptRef.current === attempt && preparationRegistration.current.active) {
                    setPreparing(old => old === attempt ? null : old); setPreparationReady(false);
                }
            };
            preparationAttemptRef.current?.retire();
            preparationAttemptRef.current = attempt;
            assertPerformancePreparationCurrent(current); setPreparationReady(false); setPreparing(attempt);
            const prepareWorkspace = (workspace) => {
                if (workspaces.has(workspace)) return workspaces.get(workspace).promise;
                const entry = {};
                entry.promise = (async () => {
                    // New tabs mount before their target read. No polling or
                    // duplicate Page/child tree is needed to acquire the reader.
                    if (!activated.has(workspace)) await new Promise(resolve => activations.set(workspace, resolve));
                    assertPerformancePreparationCurrent(current);
                    const prepareChild = childPreparations.current.get(workspace);
                    if (typeof prepareChild !== 'function') throw new Error('The visible Performance workspace is not ready to prepare.');
                    let nextOptions = null;
                    if (SINGLE_SOURCE_TABS.includes(workspace) && targetProps.watchlistIsReady) {
                        optionsPromise ||= preparePerformanceQuery(queryClient,
                            ['portfolio_history_options', targetKey, nextSourceKey, nextSourceRevision],
                            ({ signal }) => readPerformanceOptions(nextConstant, signal), current, { staleTime: 0 });
                        nextOptions = await optionsPromise;
                    }
                    assertPerformancePreparationCurrent(current);
                    const choice = nextOptions?.choices.find(item => item.value === nextBasis);
                    const constantChoice = nextOptions?.choices.find(item => item.value === 'constant');
                    const sourceReason = constantChoice?.available === false
                        ? reasonText(constantChoice.reasons) || 'This portfolio is unavailable.' : null;
                    const nextExpectation = { expected_context_digest: nextOptions?.context?.context_digest,
                        ...(nextConstant ? { constant_portfolio_source: nextConstant } : {}) };
                    const nextResolved = nextBasis === 'auto'
                        ? (nextOptions?.choices.find(item => item.value === 'ibkr')?.available ? 'ibkr' : 'constant') : nextBasis;
                    const nextChildProps = { ...targetProps, portfolioBasis: workspace === TAB.intraday ? nextResolved : nextBasis,
                        requestExpectation: nextExpectation,
                        requestEnabled: Boolean(nextOptions?.context?.context_digest && choice?.available),
                        sourceError: sourceReason ? Object.assign(new Error(sourceReason), { code: 'constant_source_unavailable' }) : null,
                        sourcePending: false, controlledLookback: initialReplayInterval(targetProps.factorContext?.factor_as_of_date),
                        analysisVersion: rawHistoryRefresh, contextKey: targetKey,
                        contextReady: Boolean(targetProps.watchlistIsReady), contextPending: false };
                    const frame = await prepareChild({ nextProps: nextChildProps, isCurrentRequest: current });
                    assertPerformancePreparationCurrent(current); entry.frame = frame; return frame;
                })();
                // A workspace left while its read is pending is optional until
                // selected again. Consume its rejection without changing authority.
                entry.promise.catch(() => {}); workspaces.set(workspace, entry); return entry.promise;
            };
            try {
                let selected; let child;
                for (;;) {
                    assertPerformancePreparationCurrent(current);
                    selected = attempt.selected;
                    const changed = new Promise(resolve => { wake = () => resolve({ changed: true }); });
                    const result = await Promise.race([prepareWorkspace(selected).then(frame => ({ frame }), error => ({ failed: true, error })), changed]);
                    assertPerformancePreparationCurrent(current);
                    if (result.changed || selected !== attempt.selected) continue;
                    if (result.failed) throw result.error;
                    child = result.frame; break;
                }
                // Once handed to the coordinator, only its two-paint/synchronous
                // commit window is fenced. Network preparation above is navigable.
                attempt.phase = 'prepared'; setPreparationReady(true);
                const children = {}; const analysisResults = {};
                workspaces.forEach((entry, workspace) => {
                    if (!entry.frame) return;
                    const { canCommit, ...frame } = entry.frame; children[workspace] = frame;
                    if (frame.portfolio) analysisResults[workspace] = { contextKey: targetKey, basis: nextBasis,
                        source: nextSourceKey, data: frame.portfolio };
                });
                const bundle = { contextKey: targetKey, userUid: targetProps.userUid,
                    factorSliceScopeKey: props.factorSliceScopeKey, children, analysisResults };
                const canCommit = () => current() && (committed || child.canCommit());
                const commit = () => {
                    if (committed) return current();
                    if (!canCommit()) return false;
                    committed = true; attempt.phase = 'committed'; setPublicationBundle(bundle); clearPreparing(); return true;
                };
                const discard = () => { attempt.retire(); clearPreparing(); };
                return { disposition: 'prepared', canCommit, commit, discard };
            } catch (error) {
                attempt.retire(); clearPreparing(); throw error;
            }
        }, preparationStamp);
    return (
        <div>
            <DashboardToolbarPortal>
                <Box role="group" aria-label="Performance workspaces" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                    {tabs.map(({ label, id: idx, Icon }) => (
                        <Box
                            component="button"
                            key={label}
                            onClick={() => setTab(idx)}
                            disabled={navigationLocked}
                            aria-pressed={tab === idx}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                minHeight: 32,
                                padding: '7px 12px',
                                border: (theme) => (usesAppStyleGrammar(theme)
                                    ? `1px solid ${tab === idx ? MSI_COMPONENT_COLORS.focusBorder : MSI_PALETTE.line}`
                                    : (tab === idx
                                        ? '1px solid rgb(var(--c-border) / 0.95)'
                                        : '1px solid rgb(var(--c-border) / 0.4)')),
                                borderRadius: (theme) => (usesAppStyleGrammar(theme)
                                    ? `${MSI_GEOMETRY.radii.navigation}px`
                                    : '6px'),
                                background: (theme) => (usesAppStyleGrammar(theme)
                                    ? (tab === idx ? MSI_PALETTE.blue100 : MSI_PALETTE.surface)
                                    : (tab === idx ? 'rgb(var(--c-bg))' : 'rgb(var(--c-elevated) / 0.35)')),
                                opacity: tab === idx ? 1 : 0.9,
                                ...SUB_TAB_LABEL_SX,
                                color: (theme) => (usesAppStyleGrammar(theme)
                                    ? (tab === idx ? MSI_PALETTE.ink900 : MSI_PALETTE.ink500)
                                    : 'rgb(var(--c-text))'),
                                letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : '0.08em'),
                                cursor: 'pointer',
                                outline: 'none',
                                transition: 'border-color 0.2s, background-color 0.2s, opacity 0.2s',
                                '&:focus-visible': {
                                    outline: (theme) => (usesAppStyleGrammar(theme)
                                        ? `2px solid ${MSI_COMPONENT_COLORS.focusBorder}`
                                        : undefined),
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            <Icon aria-hidden="true" sx={{ fontSize: 16 }} />
                            {label}
                        </Box>
                    ))}
                </Box>
            </DashboardToolbarPortal>

            <div inert={publicationPreparing ? '' : undefined}>
                {sourceContextReady && SINGLE_SOURCE_TABS.includes(tab) && optionsQuery.error && <Alert severity={isHistoryContextConflict(optionsQuery.error) ? 'info' : 'warning'} action={<Button onClick={retryHistory}>Retry</Button>}>{isHistoryContextConflict(optionsQuery.error) ? 'The portfolio context is updating. Finish loading the factor dataset, then refresh history.' : optionsQuery.error.message}</Alert>}
            {(tab === TAB.historical || tab === TAB.earnings) && visibleSourceAction?.error && <Alert severity="warning">{visibleSourceAction.error}</Alert>}
            {visitedTabs.includes(TAB.historical) && <Box sx={{ display: tab === TAB.historical ? 'contents' : 'none' }}><CurrentPortfolioHistory key={`history:${contextKey}`} {...props} active={workspaceActive(TAB.historical)} publicationPreparing={publicationPreparing} publicationSeed={acceptedBundle?.children?.[TAB.historical]?.seed ?? null} onRegisterPreparation={registerHistory} toolbarContent={sourceControls} portfolioBasis={portfolioBasis}
                    requestEnabled={historyReadEnabled} sourcePending={sourcePending} sourceError={sourceError} requestExpectation={requestExpectation} analysisVersion={analysisVersion}
                    controlledLookback={lookback} onLookbackChange={(next) => setAnalysisWindow({ contextKey, lookback: next })}
                    onPortfolioResult={receiveHistoryResult} onAuthorityRefresh={refreshHistoryAuthority} onAuthorityConflict={reconcileHistoryAuthority} /></Box>}
            {visitedTabs.includes(TAB.earnings) && <Box sx={{ display: tab === TAB.earnings ? 'contents' : 'none' }}><EarningsPerformance key={`earnings:${contextKey}`} {...props} active={workspaceActive(TAB.earnings)} publicationPreparing={publicationPreparing} onRegisterPreparation={registerEarnings} toolbarContent={sourceControls}
                    portfolioBasis={portfolioBasis} requestEnabled={historyReadEnabled} sourcePending={sourcePending} sourceError={sourceError} requestExpectation={requestExpectation} analysisVersion={analysisVersion}
                    controlledLookback={lookback} onLookbackChange={(next) => setAnalysisWindow({ contextKey, lookback: next })}
                    onPortfolioResult={receiveEarningsResult} onAuthorityRefresh={refreshEarningsAuthority} onAuthorityConflict={reconcileEarningsAuthority} /></Box>}

            {visitedTabs.includes(TAB.intraday) && <Box sx={{ display: tab === TAB.intraday ? 'grid' : 'none', gap: 1.5, mb: 1.5 }}>

                <IntradayPriceObservations key={`intraday:${contextKey}`} {...props} toolbarContent={sourceControls} toolbarEndContent={intradaySnapshot} showFactorSnapshot={false} active={workspaceActive(TAB.intraday)} publicationPreparing={publicationPreparing} publicationSeed={acceptedBundle?.children?.[TAB.intraday]?.seed ?? null} onRegisterPreparation={registerIntraday} view={intradayMode === 'prices' ? 'observations' : 'returns'} initialComponent="total" portfolioBasis={resolvedBasis} requestExpectation={requestExpectation} requestEnabled={historyReadEnabled} sourceError={sourceError} onAuthorityRefresh={refreshIntradayAuthority} />
            </Box>}
            {/* Compares its own portfolios, so its picks survive a context change for the same user. */}
            {visitedTabs.includes(TAB.compare) && <Box sx={{ display: tab === TAB.compare ? 'contents' : 'none' }}><CompareExploreTab active={workspaceActive(TAB.compare)} publicationPreparing={publicationPreparing} publicationSeed={acceptedBundle?.children?.[TAB.compare]?.seed ?? null} onRegisterPreparation={registerCompare} userUid={props.userUid} contextKey={contextKey}
                    contextReady={sourceContextReady} contextPending={!props.initialContextFailed && Boolean(props.initialContextPending || props.watchlistIsReady == null)}
                    listOfWhatIfPortfolios={savedPortfolios} savedColumns={props.compareExploreColumns} onSavedColumnsChange={props.setCompareExploreColumns} /></Box>}

            </div>
        </div>
    );
}
